/**
 * Shared i18n Configuration
 *
 * Common configuration used by both web and native platforms.
 */

import i18next, { type i18n as I18nInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from '@tonnie/schemas';

import {
  DEFAULT_LOCALE,
  DEFAULT_NAMESPACE,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from '../types';

// Import translation resources (all locales are bundled statically)
import en from '../locales/en.json';
import ptPT from '../locales/pt-PT.json';
import ptBR from '../locales/pt-BR.json';
import esES from '../locales/es-ES.json';
import itIT from '../locales/it-IT.json';

// ═══════════════════════════════════════════════════════════════════════════
// RESOURCES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Translation resources organized by locale
 *
 * NOTE: Resources are loaded under the 'common' namespace to match DEFAULT_NAMESPACE.
 * This allows t('nav.home') to resolve to en.common.nav.home correctly.
 *
 * Each locale has its own fully-translated JSON file (generated by
 * `npm run i18n:translate` from en.json and kept at exact key parity by the
 * `i18n:check` CI gate). There is intentionally NO language fallback (see
 * `fallbackLng: false` below): a missing/undefined key renders the raw key so
 * the gap fails loudly, rather than silently serving English to non-EN users.
 */
// Explicit lightweight type — annotating avoids TS serializing the (very large)
// inferred literal type of five 12k-key JSON files (TS7056).
const resources: Record<string, { common: Record<string, unknown> }> = {
  en: { common: en },
  'pt-PT': { common: ptPT },
  'pt-BR': { common: ptBR },
  'es-ES': { common: esES },
  'it-IT': { common: itIT },
};

// ═══════════════════════════════════════════════════════════════════════════
// SHARED CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Base i18next configuration options
 */
export const baseI18nConfig = {
  resources,
  // NO fallback language — a missing/undefined key renders the raw key (loud),
  // never silently the English value. Locales are held at exact parity by the
  // i18n:check CI gate, so a real key never resolves empty at runtime.
  fallbackLng: false as const,
  supportedLngs: SUPPORTED_LOCALES as unknown as string[],
  defaultNS: DEFAULT_NAMESPACE,
  interpolation: {
    escapeValue: false, // React already handles XSS
    // Numbers a rule defines, available to every string. Copy names the rule's
    // number through `{{passwordMinLength}}` / `{{passwordMaxLength}}` instead of
    // spelling it: the phone
    // said "at least 8" for years after the rule became 10 (MOB-07), and a
    // field error rendered as `t(error.message)` has no call site to pass it.
    defaultVariables: {
      passwordMinLength: PASSWORD_MIN_LENGTH,
      passwordMaxLength: PASSWORD_MAX_LENGTH,
    },
  },
  react: {
    useSuspense: false, // Don't block render while loading
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// I18N INSTANCE - SINGLETON
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Create a new i18n instance
 * This allows multiple instances if needed (e.g., for testing)
 */
export function createI18nInstance(): I18nInstance {
  return i18next.createInstance();
}

/**
 * Shared i18n instance used across the application
 * We use the default i18next instance to ensure react-i18next works correctly
 */
export const i18n = i18next;

// Initialize i18next synchronously with bundled resources. `initImmediate:
// false` makes init run synchronously so t() works on the first render (SSR +
// tests) — resources are all in-memory, so there's nothing to await.
i18next.use(initReactI18next).init({
  ...baseI18nConfig,
  lng: DEFAULT_LOCALE,
  initImmediate: false,
  debug: false,
} as Parameters<typeof i18next.init>[0]);

// ═══════════════════════════════════════════════════════════════════════════
// UTILITIES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Check if a locale is supported
 */
export function isSupportedLocale(locale: string): locale is SupportedLocale {
  return SUPPORTED_LOCALES.includes(locale as SupportedLocale);
}

/**
 * Get the best matching locale from a language code
 * Handles variants like 'en-US' -> 'en'
 */
export function getBestLocale(languageCode: string): SupportedLocale {
  // Try exact match first
  if (isSupportedLocale(languageCode)) {
    return languageCode;
  }

  // Try base language (e.g., 'en-US' -> 'en')
  const baseLanguage = languageCode.split('-')[0];
  if (baseLanguage && isSupportedLocale(baseLanguage)) {
    return baseLanguage;
  }

  // Fallback to default
  return DEFAULT_LOCALE;
}

/**
 * Change the locale on an i18n instance
 */
export async function changeLocale(instance: I18nInstance, locale: SupportedLocale): Promise<void> {
  if (!isSupportedLocale(locale)) {
    console.warn(`[i18n] Unsupported locale: ${locale}, falling back to ${DEFAULT_LOCALE}`);
    locale = DEFAULT_LOCALE;
  }

  await instance.changeLanguage(locale);
}

export { i18next };
